import React, { useEffect, useState } from "react";
import { MapPin, Gift, Info } from "lucide-react";
import { configApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

// Shows the far-pickup fee tiers up front (before driver matching) plus any loyalty
// waivers the rider has banked. Keeps riders informed so there are no surprises.
export default function PickupFeeInfo({ className = "" }) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const waivers = Number(user?.fee_waivers || 0);
  const every = Number(cfg?.waiver_every_rides || 10);
  const progress = Number(user?.completed_rides_count || 0) % every;
  const expDays = user?.waiver_expires_at ? Math.max(0, Math.ceil((new Date(user.waiver_expires_at) - Date.now()) / 86400000)) : null;

  useEffect(() => {
    let alive = true;
    configApi.fares().then((d) => alive && setCfg(d)).catch(() => {});
    return () => { alive = false; };
  }, []);

  const tiers = cfg?.pickup_fee_tiers || [
    { label: "Up to 2 km", amount_label: "Free" },
    { label: "2–5 km", amount_label: "₹30" },
    { label: "5–8 km", amount_label: "₹50" },
  ];

  return (
    <div className={`rounded-2xl border border-surface-300 bg-white p-3 ${className}`} data-testid="pickup-fee-info">
      <div className="flex items-center gap-1.5">
        <MapPin className="h-4 w-4 text-brand" />
        <span className="text-sm font-bold text-ink">Pickup fee</span>
        <span className="text-xs text-ink-muted">· only if you approve it</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2" data-testid="pickup-fee-tiers">
        {tiers.map((t) => (
          <div key={t.label} className="rounded-xl bg-surface-100 p-2 text-center">
            <p className="text-[11px] font-medium text-ink-soft">{t.label}</p>
            <p className={`text-sm font-bold ${t.amount_label === "Free" ? "text-green-600" : "text-ink"}`}>{t.amount_label}</p>
          </div>
        ))}
      </div>
      <p className="mt-2 flex items-start gap-1.5 text-[11px] text-ink-muted">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        A small fee may apply only when the nearest driver is far from your pickup. We ask first — you can always wait for a closer driver.
      </p>
      {waivers > 0 && (
        <div className="mt-2 flex items-center gap-1.5 rounded-xl bg-green-50 px-3 py-2 text-xs font-semibold text-green-700" data-testid="pickup-fee-waivers">
          <Gift className="h-3.5 w-3.5" />
          You have {waivers} free pickup{waivers === 1 ? "" : "s"} — applied automatically on your next far pickup.{expDays != null ? ` Expires in ${expDays} day${expDays === 1 ? "" : "s"}.` : ""}
        </div>
      )}
      {waivers === 0 && progress > 0 && (
        <div className="mt-2" data-testid="waiver-progress">
          <div className="flex items-center justify-between text-[11px] font-semibold text-ink-soft">
            <span>{progress === every - 1 ? "1 more ride to a free pickup!" : "Progress to a free pickup"}</span>
            <span>{progress}/{every}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-200">
            <div className="h-full rounded-full bg-brand" style={{ width: `${(progress / every) * 100}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}
